import React, { useState, useEffect, useRef } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { FaMapMarkerAlt, FaEnvelope, FaPhone, FaPaperPlane, FaCheck, FaExclamationTriangle } from "react-icons/fa";
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Contact = () => {
  // Scroll to top on mount/refresh
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: false
  });

  // Animation controls
  const controls = useAnimation();
  const formControls = useAnimation();
  const infoControls = useAnimation();

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    success: false,
    message: ''
  });

  // Map refs
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const markerRef = useRef(null);

  // Trigger animations when in view
  React.useEffect(() => {
    if (inView) {
      controls.start("visible");
      formControls.start("visible");
      infoControls.start("visible");
    } else {
      controls.start("hidden");
      formControls.start("hidden");
      infoControls.start("hidden");
    }
  }, [inView, controls, formControls, infoControls]);

  // Initialize Leaflet map
  useEffect(() => {
    if (typeof window !== 'undefined' && mapRef.current && !mapInstance.current) {
      // Create map instance with better defaults
      mapInstance.current = L.map(mapRef.current, {
        zoomControl: false,
        preferCanvas: true,
        fadeAnimation: true,
        zoomAnimation: true,
        scrollWheelZoom: false,
        attributionControl: false
      }).setView([34.4367, 35.8497], 16); // Higher zoom level

      // Add tile layer with better contrast
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        detectRetina: true,
        maxZoom: 19,
        minZoom: 10
      }).addTo(mapInstance.current);

      // Custom marker with better styling
      markerRef.current = L.marker([34.4367, 35.8497], {
        icon: new L.Icon({
          iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
          iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
          shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
          iconSize: [32, 42],
          iconAnchor: [16, 42],
          popupAnchor: [1, -34],
          shadowSize: [42, 42],
          className: 'custom-marker-icon'
        })
      }).addTo(mapInstance.current)
        .bindPopup(`
          <div class="custom-popup">
            <h4>Studio Dash</h4>
            <p>Tripoli, Lebanon</p>
            <div class="popup-arrow"></div>
          </div>
        `, {
          maxWidth: 200,
          className: 'custom-popup-wrapper'
        })
        .openPopup();

      // Custom zoom control
      L.control.zoom({
        position: 'topright',
        zoomInText: '+',
        zoomOutText: '-'
      }).addTo(mapInstance.current);

      // Custom scale control
      L.control.scale({
        position: 'bottomleft',
        maxWidth: 150,
        metric: true,
        imperial: false,
        updateWhenIdle: true
      }).addTo(mapInstance.current);

      // Handle map resize
      const resizeObserver = new ResizeObserver(() => {
        mapInstance.current.invalidateSize();
      });
      resizeObserver.observe(mapRef.current);

      return () => {
        if (mapInstance.current) {
          mapInstance.current.remove();
          mapInstance.current = null;
        }
        resizeObserver.disconnect();
      };
    }
  }, []);

  // Animation variants (keep your existing variants)
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 40, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.8,
        ease: [0.6, -0.05, 0.01, 0.99]
      }
    }
  };

  const fadeInVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1, 
      transition: { 
        duration: 1,
        ease: "easeOut"
      } 
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: (i) => ({
      y: 0,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        duration: 0.6,
        ease: "backOut"
      }
    })
  };

  const titleLineVariants = {
    hidden: { scaleX: 0, originX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        delay: 0.4,
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const floatingVariants = {
    float: {
      y: [0, -15, 0],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = 'Subject is required';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.length < 10) {
      newErrors.message = 'Message should be at least 10 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Handle form submission using mailto:
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    setSubmitStatus({ success: false, message: '' });

    try {
      const { name, email, subject, message } = formData;
      
      // Construct mailto link
      const mailtoLink = `mailto:studiodashweb@gmail.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(
        `Name: ${name}\nEmail: ${email}\n\nMessage:\n${message}`
      )}`;
      
      // Open user's email client
      window.location.href = mailtoLink;
      
      // Show success message
      setSubmitStatus({
        success: true,
        message: 'Your email client has opened with a pre-filled message. Please click send to complete the process.'
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error opening email client:', error);
      setSubmitStatus({
        success: false,
        message: 'Failed to open email client. Please try again or email us directly at studiodashweb@gmail.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.main
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
      className="contact-container"
    >
      {/* Hero Section */}
      <section className="contact-hero-section">
        <motion.div 
          className="hero-bg-pattern"
          animate={{
            backgroundPosition: ['0% 0%', '100% 100%'],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
        />

        <div className="hero-content">
          <motion.div variants={itemVariants}>
            <motion.h1
              className="hero-title"
              variants={itemVariants}
            >
              Get in <span className="text-accent">Touch</span>
            </motion.h1>
            <motion.p
              className="hero-subtitle"
              variants={itemVariants}
            >
              We'd love to hear about your project and discuss how we can help bring it to life.
            </motion.p>
          </motion.div>
        </div>

        <motion.div 
          className="scroll-indicator"
          animate={{
            y: [0, 10, 0],
            opacity: [0.6, 1, 0.6]
          }}
          transition={{
            duration: 2,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <span>Scroll</span>
          <div className="scroll-line" />
        </motion.div>
      </section>

      {/* Contact Content */}
      <section className="contact-content-section">
        <AnimatePresence>
          <motion.div
            className="floating-envelope"
            variants={floatingVariants}
            animate="float"
            initial={{ opacity: 0, y: 50 }}
            animate={{
              opacity: 1,
              y: 0,
              transition: { duration: 1 }
            }}
          >
            <FaPaperPlane size={60} className="text-accent" />
          </motion.div>
        </AnimatePresence>

        <div className="contact-grid">
          {/* Contact Form */}
          <motion.div
            className="contact-form-container"
            initial="hidden"
            animate={formControls}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <h2 className="section-title">
                Send Us a <span className="text-accent">Message</span>
              </h2>
              <motion.div 
                className="title-line"
                variants={titleLineVariants}
              />
              <p className="section-subtitle">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
            </motion.div>

            <form className="contact-form" onSubmit={handleSubmit} noValidate>
              <motion.div 
                className="form-group"
                variants={itemVariants}
              >
                <label htmlFor="name">Your Name</label>
                <input 
                  type="text" 
                  id="name" 
                  name="name" 
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter your name" 
                  className={`form-input ${errors.name ? 'error' : ''}`}
                  required
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </motion.div>

              <motion.div 
                className="form-group"
                variants={itemVariants}
              >
                <label htmlFor="email">Email Address</label>
                <input 
                  type="email" 
                  id="email" 
                  name="email" 
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your email" 
                  className={`form-input ${errors.email ? 'error' : ''}`}
                  required
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </motion.div>

              <motion.div 
                className="form-group"
                variants={itemVariants}
              >
                <label htmlFor="subject">Subject</label>
                <input 
                  type="text" 
                  id="subject" 
                  name="subject" 
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="What's this about?" 
                  className={`form-input ${errors.subject ? 'error' : ''}`}
                  required
                />
                {errors.subject && <span className="error-message">{errors.subject}</span>}
              </motion.div>

              <motion.div 
                className="form-group"
                variants={itemVariants}
              >
                <label htmlFor="message">Your Message</label>
                <textarea 
                  id="message" 
                  name="message" 
                  value={formData.message}
                  onChange={handleChange}
                  rows="5" 
                  placeholder="Tell us about your project..." 
                  className={`form-textarea ${errors.message ? 'error' : ''}`}
                  required
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </motion.div>

              {/* Status message */}
              {submitStatus.message && (
                <motion.div 
                  className={`status-message ${submitStatus.success ? 'success' : 'error'}`}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {submitStatus.success ? (
                    <FaCheck className="status-icon" />
                  ) : (
                    <FaExclamationTriangle className="status-icon" />
                  )}
                  {submitStatus.message}
                </motion.div>
              )}

              <motion.div variants={itemVariants}>
                <motion.button 
                  type="submit" 
                  className="submit-button"
                  disabled={isSubmitting}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 6px 25px rgba(237, 121, 49, 0.5)"
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span>{isSubmitting ? 'Opening Email...' : 'Send Message'}</span>
                  <motion.span
                    className="button-hover-effect"
                    initial={{ x: '-100%' }}
                    whileHover={{ x: '0%' }}
                    transition={{ duration: 0.6 }}
                  />
                </motion.button>
              </motion.div>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            className="contact-info-container"
            initial="hidden"
            animate={infoControls}
            variants={containerVariants}
          >
            <motion.div variants={itemVariants}>
              <h2 className="section-title">
                Contact <span className="text-accent">Information</span>
              </h2>
              <motion.div 
                className="title-line"
                variants={titleLineVariants}
              />
              <p className="section-subtitle">
                Here's how you can reach us directly.
              </p>
            </motion.div>

            <div className="contact-info-cards">
              <motion.div 
                className="info-card"
                variants={cardVariants}
                custom={0}
                whileHover={{ 
                  y: -10,
                  backgroundColor: "rgba(237, 121, 49, 0.1)",
                  borderColor: "rgba(237, 121, 49, 0.5)"
                }}
              >
                <div className="info-icon">
                  <FaMapMarkerAlt size={30} className="text-accent" />
                </div>
                <h3>Address</h3>
                <p>Tripoli, Lebanon</p>
              </motion.div>

              <motion.div 
                className="info-card"
                variants={cardVariants}
                custom={1}
                whileHover={{ 
                  y: -10,
                  backgroundColor: "rgba(237, 121, 49, 0.1)",
                  borderColor: "rgba(237, 121, 49, 0.5)"
                }}
              >
                <div className="info-icon">
                  <FaEnvelope size={30} className="text-accent" />
                </div>
                <h3>Email</h3>
                <p>studiodashweb@gmail.com</p>
                <p className="info-note">For project inquiries</p>
              </motion.div>

              <motion.div 
                className="info-card"
                variants={cardVariants}
                custom={2}
                whileHover={{ 
                  y: -10,
                  backgroundColor: "rgba(237, 121, 49, 0.1)",
                  borderColor: "rgba(237, 121, 49, 0.5)"
                }}
              >
                <div className="info-icon">
                  <FaPhone size={30} className="text-accent" />
                </div>
                <h3>Phone</h3>
                <p>+961 76536462</p>
              </motion.div>
            </div>

            <motion.div className="map-container" variants={fadeInVariants}>
              <div 
                ref={mapRef}
                id="map"
                style={{ 
                  height: '400px',
                  width: '100%',
                  borderRadius: '0.75rem',
                  zIndex: 1
                }}
              />
            </motion.div>
          </motion.div>
        </div>
      </section>

      

      <style jsx global>{`
        /* Base Styles */
        .contact-container {
          font-family: 'Inter', sans-serif;
          background-color: var(--primary);
          color: var(--secondary);
        }

        /* Hero Section */
        .contact-hero-section {
          position: relative;
          padding: 10rem 2rem;
          background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
          overflow: hidden;
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
        }

        .hero-bg-pattern {
          position: absolute;
          inset: 0;
          opacity: 0.05;
          background-image: radial-gradient(circle at center, var(--accent) 1px, transparent 1px);
          background-size: 20px 20px;
        }

        .hero-content {
          max-width: 1200px;
          margin: 0 auto;
          position: relative;
          z-index: 10;
          text-align: center;
        }

        .hero-title {
          font-size: 4rem;
          font-weight: var(--font-extra-bold);
          margin-bottom: 1.5rem;
          color: white;
          letter-spacing: 0.05em;
          line-height: 1.2;
          text-shadow: 0 2px 10px rgba(237, 121, 49, 0.3);
        }

        .hero-subtitle {
          font-size: 1.5rem;
          color: var(--grey-60);
          margin-bottom: 2rem;
          max-width: 800px;
          margin-left: auto;
          margin-right: auto;
          line-height: 1.6;
          font-weight: var(--font-light);
        }

        .scroll-indicator {
          position: absolute;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          color: var(--grey-60);
          font-size: 0.875rem;
        }

        .scroll-line {
          width: 1px;
          height: 50px;
          background: linear-gradient(to bottom, var(--accent), transparent);
          margin-top: 0.5rem;
        }

        /* Contact Content Section */
        .contact-content-section {
          padding: 8rem 2rem;
          position: relative;
          overflow: hidden;
          background-color: var(--primary);
        }

        .floating-envelope {
          position: absolute;
          top: -30px;
          right: 10%;
          opacity: 0.2;
          z-index: 0;
        }

        .contact-grid {
          max-width: 1200px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 4rem;
          position: relative;
          z-index: 1;
        }

        .contact-form-container, .contact-info-container {
          display: flex;
          flex-direction: column;
          gap: 2rem;
        }

        .section-title {
          font-size: 2.5rem;
          font-weight: var(--font-bold);
          color: white;
          margin-bottom: 0.5rem;
        }

        .title-line {
          width: 80px;
          height: 4px;
          background-color: var(--accent);
          margin-bottom: 2rem;
          transform-origin: left;
        }

        .section-subtitle {
          color: var(--accent);
          font-weight: var(--font-semi-bold);
          font-size: 1.25rem;
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
        }

        .form-group label {
          font-weight: var(--font-medium);
          color: var(--grey-60);
        }

        .form-input, .form-textarea {
          padding: 1rem;
          background-color: var(--grey-7);
          border: 1px solid var(--grey-10);
          border-radius: 0.5rem;
          color: white;
          font-size: 1rem;
          transition: all 0.3s ease;
        }

        .form-input.error, .form-textarea.error {
          border-color: #e74c3c !important;
          background-color: rgba(231, 76, 60, 0.05) !important;
        }

        .form-input:focus, .form-textarea:focus {
          outline: none;
          border-color: var(--accent);
          box-shadow: 0 0 0 3px rgba(237, 121, 49, 0.2);
        }

        .form-textarea {
          resize: vertical;
          min-height: 150px;
        }

        .error-message {
          color: #e74c3c;
          font-size: 0.875rem;
          margin-top: 0.25rem;
          display: block;
        }

        .submit-button {
          position: relative;
          padding: 1rem 2.5rem;
          background-color: var(--accent);
          color: var(--primary);
          font-weight: var(--font-semi-bold);
          border-radius: 9999px;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          box-shadow: 0 4px 15px rgba(237, 121, 49, 0.4);
          transition: all 0.3s ease;
          overflow: hidden;
          z-index: 1;
          width: 100%;
          margin-top: 1rem;
        }

        .button-hover-effect {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          background-color: rgba(255, 255, 255, 0.2);
          border-radius: 9999px;
          z-index: -1;
        }

        .status-message {
          padding: 1rem;
          border-radius: 0.5rem;
          margin: 1rem 0;
          font-weight: var(--font-medium);
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .status-message.success {
          background-color: rgba(46, 204, 113, 0.2);
          color: #2ecc71;
          border: 1px solid #2ecc71;
        }

        .status-message.error {
          background-color: rgba(231, 76, 60, 0.2);
          color: #e74c3c;
          border: 1px solid #e74c3c;
        }

        .status-icon {
          font-size: 1.2rem;
        }

        .contact-info-cards {
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          margin-bottom: 2rem;
        }

        .info-card {
          background-color: var(--grey-7);
          padding: 2rem;
          border-radius: 0.75rem;
          border: 1px solid var(--grey-10);
          transition: all 0.3s ease;
        }

        .info-icon {
          margin-bottom: 1rem;
        }

        .info-card h3 {
          font-weight: var(--font-semi-bold);
          color: white;
          margin-bottom: 0.5rem;
          font-size: 1.25rem;
        }

        .info-card p {
          font-size: 1rem;
          color: var(--grey-60);
          line-height: 1.5;
        }

        .info-note {
          font-size: 0.875rem;
          color: var(--accent);
          margin-top: 0.5rem;
          font-style: italic;
        }

        /* Enhanced Map Styles */
        .map-container {
          margin-top: 2rem;
          border-radius: 0.75rem;
          overflow: hidden;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          height: 400px;
          position: relative;
        }

        .leaflet-container {
          background-color: var(--grey-7) !important;
          color: var(--secondary);
        }

        .leaflet-popup-content {
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: var(--secondary);
        }

        .leaflet-popup-content-wrapper {
          background: var(--grey-7);
          border-radius: 8px;
          box-shadow: 0 3px 14px rgba(0, 0, 0, 0.4);
          border: 1px solid var(--grey-10);
        }

        .leaflet-popup-tip {
          background: var(--grey-7);
          border: 1px solid var(--grey-10);
        }

        .leaflet-bar {
          box-shadow: none !important;
          border: 1px solid var(--grey-10) !important;
          background-color: var(--grey-7) !important;
          border-radius: 4px !important;
          overflow: hidden;
        }

        .leaflet-bar a {
          background-color: var(--grey-7) !important;
          color: white !important;
          border-bottom: 1px solid var(--grey-10) !important;
          transition: all 0.2s ease;
        }

        .leaflet-bar a:hover {
          background-color: var(--grey-10) !important;
        }

        .leaflet-bar a.leaflet-disabled {
          background-color: var(--grey-10) !important;
          color: var(--grey-40) !important;
        }

        .leaflet-control-zoom-in,
        .leaflet-control-zoom-out {
          font-size: 18px !important;
          line-height: 26px !important;
        }

        .leaflet-control-scale-line {
          background: rgba(0, 0, 0, 0.5);
          color: white;
          border: none;
          border-radius: 3px;
          font-size: 11px;
        }

        .custom-marker-icon {
          filter: hue-rotate(180deg) brightness(1.2);
        }

        /* CTA Section */
        .contact-cta-section {
          padding: 10rem 2rem;
          background: linear-gradient(135deg, #1a1a1a 0%, #0d0d0d 100%);
          color: white;
          text-align: center;
          position: relative;
          overflow: hidden;
        }

        .ring {
          position: absolute;
          border-radius: 50%;
          border: 1px solid rgba(237, 121, 49, 0.2);
          z-index: 0;
        }

        .ring-1 {
          top: 50%;
          left: 20%;
          width: 300px;
          height: 300px;
        }

        .ring-2 {
          top: 30%;
          right: 15%;
          width: 400px;
          height: 400px;
        }

        .cta-content {
          max-width: 800px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .cta-title {
          font-size: 3rem;
          font-weight: var(--font-bold);
          margin-bottom: 1.5rem;
          line-height: 1.2;
        }

        .cta-text {
          font-size: 1.25rem;
          color: var(--grey-60);
          margin-bottom: 2.5rem;
          line-height: 1.6;
        }

        .cta-button {
          position: relative;
          padding: 1rem 2.5rem;
          background-color: var(--accent);
          color: var(--primary);
          font-weight: var(--font-semi-bold);
          border-radius: 9999px;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          box-shadow: 0 4px 15px rgba(237, 121, 49, 0.4);
          transition: all 0.3s ease;
          overflow: hidden;
          z-index: 1;
        }

        .text-accent {
          color: var(--accent);
        }

        /* Responsive Styles */
        @media (max-width: 980px) {
          .contact-grid {
            grid-template-columns: 1fr;
          }

          .hero-title {
            font-size: 2.5rem;
          }

          .section-title {
            font-size: 2rem;
          }

          .floating-envelope {
            display: none;
          }
        }

        @media (max-width: 640px) {
          .contact-hero-section, .contact-content-section, .contact-cta-section {
            padding: 6rem 1.5rem;
          }

          .hero-title {
            font-size: 2rem;
          }

          .section-title {
            font-size: 1.75rem;
          }

          .info-card {
            padding: 1.5rem;
          }

          .map-container {
            height: 300px;
          }
        }
      `}</style>
    </motion.main>
  );
};

export default Contact;